/*
 * LabRequirementsWidget — dashboard card that lists everything the
 * IT teacher requires this student to bring or buy. Used by both
 * StudentDashboard (single student) and ParentDashboard (one per
 * linked child).
 *
 * Data source: GET /api/itlab/student-requirements/mine?studentId=X
 * The endpoint is gated server-side so a Student must match their
 * X-Linked-Entity-Id, and a Parent must own a ParentStudent link to
 * the requested studentId. We pass studentId straight from session,
 * never from URL params, so the row-level gate is honoured by the
 * normal fetch wrapper that injects the auth headers.
 *
 * Premium UX targets:
 *   - Soft-gold paper card matching the ITLab page palette so the
 *     widget feels part of the same suite.
 *   - Mandatory items pin to the top with a red badge (same idea as
 *     the SEVERE-allergy emergency ribbon on the clinic page).
 *   - Empty state is informative, never blank.
 *   - prefers-reduced-motion honoured via the CSS module.
 */
import { useEffect, useState } from "react";
import API_BASE from "../../../config";
import w from "./LabRequirementsWidget.module.css";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return null; }
}

function peso(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return "₱" + n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/**
 * Returns true when the due date has already passed. Compared at the
 * day boundary so a "due today" item stays in the urgent-not-overdue
 * bracket until tomorrow morning.
 */
function isOverdue(iso) {
  if (!iso) return false;
  try {
    const due = new Date(iso);
    if (Number.isNaN(due.getTime())) return false;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return due < startOfToday;
  } catch { return false; }
}

export default function LabRequirementsWidget({
  studentId,
  childName = null,           // when set, the widget label shows "for <name>"
  collapsedByDefault = false, // ParentDashboard renders many children — start collapsed
}) {
  const [state, setState] = useState({ loading: true, rows: [], error: "" });
  const [open, setOpen] = useState(!collapsedByDefault);

  useEffect(() => {
    if (!studentId) {
      setState({ loading: false, rows: [], error: "" });
      return undefined;
    }
    let cancelled = false;
    setState(p => ({ ...p, loading: true }));
    fetch(`${API_BASE}/api/itlab/student-requirements/mine?studentId=${studentId}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => { if (!cancelled) setState({ loading: false, rows: Array.isArray(rows) ? rows : [], error: "" }); })
      .catch(e => { if (!cancelled) setState({ loading: false, rows: [], error: e.message || "Failed to load" }); });
    return () => { cancelled = true; };
  }, [studentId]);

  const { loading, rows, error } = state;

  // Hide the widget entirely when nothing applies AND nothing is
  // loading — keeps the dashboard from showing a "no items required"
  // card for the vast majority of students. The IT teacher publishes
  // requirements only when they exist, so the card should only
  // surface when there's something to act on.
  if (!loading && !error && rows.length === 0) return null;

  const mandatoryCount = rows.filter(r => r.mandatory).length;

  return (
    <section className={w.card} aria-label="Computer lab requirements">
      <button
        type="button"
        className={w.header}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <div className={w.headerLeft}>
          <div className={w.icon} aria-hidden>📋</div>
          <div>
            <div className={w.eyebrow}>Computer Lab</div>
            <div className={w.title}>
              Items required {childName ? `for ${childName}` : "for you"}
            </div>
            <div className={w.sub}>
              {loading
                ? "Loading…"
                : error
                  ? "Couldn't load — try again later"
                  : `${rows.length} item${rows.length === 1 ? "" : "s"}${mandatoryCount > 0 ? ` · ${mandatoryCount} mandatory` : ""}`}
            </div>
          </div>
        </div>
        <div className={w.headerRight}>
          {mandatoryCount > 0 && (
            <span className={`${w.pill} ${w.pillRed}`}>{mandatoryCount} mandatory</span>
          )}
          <span className={`${w.chev} ${open ? w.chevOpen : ""}`} aria-hidden>▾</span>
        </div>
      </button>

      {open && (
        <div className={w.body}>
          {error && <div className={w.error}>{error}</div>}

          {!loading && !error && (
            <ul className={w.list}>
              {rows.map(r => {
                const overdue = isOverdue(r.dueDate);
                const due = fmtDate(r.dueDate);
                const cost = peso(r.estimatedCost);
                return (
                  <li key={r.id} className={w.row}>
                    <div className={r.mandatory ? `${w.bubble} ${w.bubbleRed}` : w.bubble} aria-hidden>
                      {r.mandatory ? "★" : "•"}
                    </div>
                    <div className={w.rowMain}>
                      <div className={w.rowTitle}>{r.label}</div>
                      {r.description && <div className={w.rowDesc}>{r.description}</div>}
                      <div className={w.rowMeta}>
                        {r.mandatory
                          ? <span className={`${w.pill} ${w.pillRed}`}>Mandatory</span>
                          : <span className={`${w.pill} ${w.pillNeutral}`}>Optional</span>}
                        {due && (
                          <span className={`${w.pill} ${overdue ? w.pillCritical : w.pillGold}`}>
                            {overdue ? "Past due" : "Due"} {due}
                          </span>
                        )}
                        {cost && (
                          <span className={`${w.pill} ${w.pillNeutral}`}>Est. {cost}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className={w.footHint}>
            Published by the school's IT / Computer Lab team. Bring the
            mandatory items by their due date to avoid disruption to lab
            activities.
          </div>
        </div>
      )}
    </section>
  );
}
